"use client";

import React, { useState, useEffect, useMemo } from "react";
import {
  X,
  Mail,
  Phone,
  Building2,
  UserCheck,
  Globe,
  Tag,
  Clock,
  History,
  AlertTriangle,
  Save,
  CheckCircle2,
  FileText,
  MessageSquare,
  CreditCard,
  Trash2,
  Lock,
} from "lucide-react";

import { Lead, AuditLog } from "@/lib/db";
import { Button } from "@/components/ui/Button";
import { convertCurrency, formatCurrencyValue } from "@/lib/currency";
import { WorkspaceConfig } from "@/lib/workspacePresets";

interface TeamMemberOption {
  id: number;
  username: string;
  full_name: string;
  role: string;
  is_active?: number;
}

interface LeadDetailModalProps {
  leadId: number;
  onClose: () => void;
  onLeadUpdated: () => void;
  onOpenChat?: (lead: Lead) => void;
  pipelineStages?: { name: string; color: string }[];
  fieldLabels?: Record<string, string>;
  workspaceConfig?: WorkspaceConfig;
  isPlatformAdmin?: boolean;
  currentUser?: { id: number; username: string; role: string; fullName: string };
  teamMembers?: TeamMemberOption[];
}

const DEFAULT_STAGES = [
  "New Lead",
  "Discovery Call",
  "Proposal Sent",
  "Negotiation",
  "Closed Won",
  "Closed Lost",
];

export default function LeadDetailModal({
  leadId,
  onClose,
  onLeadUpdated,
  onOpenChat,
  pipelineStages,
  fieldLabels = {},
  workspaceConfig,
  isPlatformAdmin = false,
  currentUser,
  teamMembers,
}: LeadDetailModalProps) {
  const [teamList, setTeamList] = useState<TeamMemberOption[]>(teamMembers || []);

  useEffect(() => {
    if (teamMembers && teamMembers.length > 0) {
      setTeamList(teamMembers);
    } else {
      fetch("/api/crm/team")
        .then((res) => res.json())
        .then((data) => {
          if (data.success && data.team) {
            setTeamList(data.team);
          }
        })
        .catch(() => {});
    }
  }, [teamMembers]);

  const userRole = (currentUser?.role || (isPlatformAdmin ? "admin" : "agent")).toLowerCase();
  const isAdmin = userRole === "admin" || isPlatformAdmin;
  const isManager = userRole === "manager";
  const isAgent = !isAdmin && !isManager;

  // Role Permissions Matrix:
  // Super Admin: Can assign to anyone in company (Admin, Managers, Agents) or Unassigned
  // Sales Manager: Can assign to Sales Agents, Managers (or self) or Unassigned
  // Sales Agent: Cannot reassign leads! Dropdown is disabled / read-only.
  const selectableMembers = useMemo(() => {
    return teamList.filter((m) => {
      if (m.is_active === 0) return false;
      const memberRole = (m.role || "agent").toLowerCase();
      if (isAdmin) return true;
      if (isManager) {
        return memberRole === "agent" || memberRole === "sales" || memberRole === "manager" || m.id === currentUser?.id;
      }
      return m.id === currentUser?.id || memberRole === "agent";
    });
  }, [teamList, isAdmin, isManager, currentUser?.id]);

  const formatRoleLabel = (role: string) => {
    const r = (role || "").toLowerCase();
    if (r === "admin") return "Super Admin";
    if (r === "manager") return "Sales Manager";
    return "Sales Agent";
  };
  const [lead, setLead] = useState<Lead | null>(null);
  const [auditLogs, setAuditLogs] = useState<AuditLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [balanceSending, setBalanceSending] = useState(false);
  const [balanceSuccess, setBalanceSuccess] = useState<string | null>(null);
  const [error, setError] = useState("");

  // Editable Form Fields
  const [status, setStatus] = useState<string>("New Lead");
  const [priority, setPriority] = useState<Lead["priority"]>("WARM");
  const [dealValue, setDealValue] = useState<number>(0);
  const [currency, setCurrency] = useState<string>("USD");
  const [assignedRep, setAssignedRep] = useState<string>("");
  const [serviceType, setServiceType] = useState<string>("");
  const [quantity, setQuantity] = useState<string | number>("");
  const [unit, setUnit] = useState<string>("");
  const [countryOrCity, setCountryOrCity] = useState<string>("");
  const [notes, setNotes] = useState<string>("");
  const [followupDate, setFollowupDate] = useState<string>("");
  const [customFieldsData, setCustomFieldsData] = useState<Record<string, string>>({});
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    async function fetchLeadDetails() {
      setLoading(true);
      setError("");
      try {
        const res = await fetch(`/api/crm/leads/${leadId}`);
        const data = await res.json();
        if (res.ok && data.success) {
          setLead(data.lead);
          setAuditLogs(data.auditLogs || []);

          // Populate editable states
          setStatus(data.lead.status);
          setPriority(data.lead.priority);
          setDealValue(data.lead.estimated_deal_value || 0);
          setCurrency(data.lead.currency || (data.lead.budget_range?.includes("₹") ? "INR" : "USD"));
          setAssignedRep(data.lead.assigned_rep || "");
          setServiceType(data.lead.service_type || "");
          setQuantity(data.lead.quantity !== null && data.lead.quantity !== undefined ? data.lead.quantity : "");
          setUnit(data.lead.unit || "");
          setCountryOrCity(data.lead.country_or_city || "");
          setNotes(data.lead.notes || "");
          setFollowupDate(data.lead.next_followup_date || "");

          if (data.lead.custom_fields) {
            try {
              const parsed = typeof data.lead.custom_fields === "string" ? JSON.parse(data.lead.custom_fields) : data.lead.custom_fields;
              setCustomFieldsData(parsed || {});
            } catch {
              setCustomFieldsData({});
            }
          } else {
            setCustomFieldsData({});
          }
        } else {
          setError(data.error || "Failed to load lead details.");
        }
      } catch {
        setError("Network error fetching lead.");
      } finally {
        setLoading(false);
      }
    }

    fetchLeadDetails();
  }, [leadId]);

  const handleSave = async () => {
    setSaving(true);
    setSaveSuccess(false);
    setError("");

    try {
      const res = await fetch(`/api/crm/leads/${leadId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          status,
          priority,
          estimated_deal_value: dealValue,
          currency,
          assigned_rep: assignedRep || null,
          service_type: serviceType || null,
          quantity: quantity !== "" && quantity !== null ? Number(quantity) : null,
          unit: unit || null,
          country_or_city: countryOrCity || null,
          notes: notes || null,
          next_followup_date: followupDate || null,
          custom_fields: customFieldsData,
        }),
      });

      const data = await res.json();

      if (res.ok && data.success) {
        setLead(data.lead);
        setSaveSuccess(true);
        onLeadUpdated();

        // Refresh audit logs
        const auditRes = await fetch(`/api/crm/leads/${leadId}`);
        const auditData = await auditRes.json();
        if (auditData.auditLogs) {
          setAuditLogs(auditData.auditLogs);
        }

        setTimeout(() => setSaveSuccess(false), 3000);
      } else {
        setError(data.error || "Failed to update lead.");
      }
    } catch {
      setError("Network error updating lead.");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteLead = async () => {
    if (!window.confirm("Are you sure you want to permanently delete this lead?")) {
      return;
    }

    setDeleting(true);
    setError("");

    try {
      const res = await fetch(`/api/crm/leads/${leadId}`, {
        method: "DELETE",
      });

      const data = await res.json();
      if (res.ok && data.success) {
        onLeadUpdated();
        onClose();
      } else {
        setError(data.error || "Failed to delete lead.");
      }
    } catch {
      setError("Network error while deleting lead.");
    } finally {
      setDeleting(false);
    }
  };

  const handleDispatchBalanceFollowUp = async () => {
    if (!lead) return;
    setBalanceSending(true);
    setBalanceSuccess(null);
    setError("");

    try {
      const res = await fetch(`/api/crm/leads/${lead.id}/balance-followup`, {
        method: "POST",
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setBalanceSuccess(data.message);
        onLeadUpdated();

        // Refresh audit logs
        const auditRes = await fetch(`/api/crm/leads/${leadId}`);
        const auditData = await auditRes.json();
        if (auditData.auditLogs) {
          setAuditLogs(auditData.auditLogs);
        }
        if (auditData.lead) {
          setLead(auditData.lead);
          setFollowupDate(auditData.lead.next_followup_date || "");
        }

        setTimeout(() => setBalanceSuccess(null), 5000);
      } else {
        setError(data.error || "Failed to dispatch balance follow-up.");
      }
    } catch {
      setError("Network error dispatching balance follow-up.");
    } finally {
      setBalanceSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/70 backdrop-blur-md p-4 sm:p-6 overflow-y-auto">
      <div className="relative w-full max-w-4xl max-h-[92vh] flex flex-col bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl shadow-2xl overflow-hidden">
        
        {/* Modal Top Navigation */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/50">
          <div className="flex items-center gap-3">
            <span className="text-xs font-mono font-bold px-2.5 py-1 rounded bg-slate-200 dark:bg-slate-800 text-slate-700 dark:text-slate-300">
              LEAD #{leadId}
            </span>
            {lead?.lead_mode === "TEST" && (
              <span className="text-[11px] font-bold px-2 py-0.5 rounded bg-amber-500/10 text-amber-600 dark:text-amber-400 border border-amber-500/20">
                TEST LEAD
              </span>
            )}
            {lead?.is_duplicate === 1 && (
              <span className="text-[11px] font-bold px-2 py-0.5 rounded bg-rose-500/10 text-rose-600 dark:text-rose-400 border border-rose-500/20 flex items-center gap-1">
                <AlertTriangle className="w-3 h-3" /> Duplicate of #{lead.duplicate_of_id}
              </span>
            )}
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Modal Content Scroll Area */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6 text-slate-800 dark:text-slate-200">
          {loading ? (
            <div className="py-20 flex flex-col items-center justify-center text-slate-400">
              <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mb-3" />
              <p className="text-sm">Loading lead intelligence data...</p>
            </div>
          ) : error && !lead ? (
            <div className="p-4 bg-red-500/10 border border-red-500/20 text-red-600 rounded-xl text-sm">
              {error}
            </div>
          ) : lead ? (
            <>
              {/* Header Title Info */}
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-4 border-b border-slate-200 dark:border-slate-800">
                <div>
                  <h2 className="text-2xl font-bold text-slate-900 dark:text-white">{lead.name}</h2>
                  <div className="flex items-center gap-4 mt-1 text-xs text-slate-500 dark:text-slate-400">
                    <span className="flex items-center gap-1">
                      <Building2 className="w-3.5 h-3.5" />
                      {lead.company_name || "Independent"}
                    </span>
                    <span>•</span>
                    <span className="font-semibold text-primary">{lead.service_type}</span>
                    <span>•</span>
                    <span className="flex items-center gap-1">
                      <Clock className="w-3.5 h-3.5" />
                      Created: {new Date(lead.created_at).toLocaleString()}
                    </span>
                  </div>
                </div>

                {/* Direct Action Links */}
                <div className="flex items-center gap-2">
                  <a
                    href={`mailto:${lead.email}`}
                    className="px-3 py-1.5 rounded-lg bg-primary/10 text-primary text-xs font-semibold hover:bg-primary/20 transition-colors flex items-center gap-1.5"
                  >
                    <Mail className="w-3.5 h-3.5" /> Email
                  </a>
                  {lead.phone && (
                    <a
                      href={`tel:${lead.phone}`}
                      className="px-3 py-1.5 rounded-lg bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 text-xs font-semibold hover:bg-emerald-500/20 transition-colors flex items-center gap-1.5"
                    >
                      <Phone className="w-3.5 h-3.5" /> Call
                    </a>
                  )}
                  {lead.phone && onOpenChat && (
                    <button
                      type="button"
                      onClick={() => onOpenChat(lead)}
                      className="px-3 py-1.5 rounded-lg bg-emerald-600 text-white text-xs font-bold hover:bg-emerald-500 shadow-sm transition-all flex items-center gap-1.5 cursor-pointer"
                    >
                      <MessageSquare className="w-3.5 h-3.5" /> WhatsApp Chat
                    </button>
                  )}
                </div>
              </div>

              {/* Editable Pipeline Controls */}
              <div className="p-5 rounded-xl bg-slate-50 dark:bg-slate-950/60 border border-slate-200 dark:border-slate-800 space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 flex items-center gap-1.5">
                    <Tag className="w-3.5 h-3.5 text-primary" /> Pipeline Controls & Assignment
                  </h3>
                  {saveSuccess && (
                    <span className="text-xs text-emerald-600 dark:text-emerald-400 font-semibold flex items-center gap-1 animate-pulse">
                      <CheckCircle2 className="w-3.5 h-3.5" /> Changes saved to audit trail!
                    </span>
                  )}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3.5">
                  {/* Status Dropdown */}
                  <div>
                    <label className="block text-[11px] font-semibold text-slate-500 uppercase mb-1">
                      Pipeline Stage
                    </label>
                    <select
                      value={status}
                      onChange={(e) => setStatus(e.target.value)}
                      className="w-full px-3 py-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg text-xs font-semibold text-slate-800 dark:text-slate-100 focus:ring-2 focus:ring-primary/50"
                    >
                      {(pipelineStages && pipelineStages.length > 0
                        ? pipelineStages.map((s) => s.name)
                        : DEFAULT_STAGES
                      ).map((st) => (
                        <option key={st} value={st}>
                          {st}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Priority Dropdown */}
                  <div>
                    <label className="block text-[11px] font-semibold text-slate-500 uppercase mb-1">
                      Lead Priority
                    </label>
                    <select
                      value={priority}
                      onChange={(e) => setPriority(e.target.value as Lead["priority"])}
                      className="w-full px-3 py-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg text-xs font-semibold text-slate-800 dark:text-slate-100 focus:ring-2 focus:ring-primary/50"
                    >
                      <option value="HOT">🔥 HOT</option>
                      <option value="WARM">🟡 WARM</option>
                      <option value="COLD">❄️ COLD</option>
                    </select>
                  </div>

                  {/* Currency Dropdown */}
                  <div>
                    <label className="block text-[11px] font-semibold text-slate-500 uppercase mb-1">
                      Currency
                    </label>
                    <select
                      value={currency}
                      onChange={(e) => {
                        const newCurr = e.target.value;
                        if (dealValue > 0 && newCurr !== currency) {
                          setDealValue(convertCurrency(dealValue, currency, newCurr));
                        }
                        setCurrency(newCurr);
                      }}
                      className="w-full px-3 py-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg text-xs font-semibold text-slate-800 dark:text-slate-100 focus:ring-2 focus:ring-primary/50 cursor-pointer"
                    >
                      <option value="AED">🇦🇪 AED (د.إ)</option>
                      <option value="USD">🇺🇸 USD ($)</option>
                      <option value="INR">🇮🇳 INR (₹)</option>
                    </select>
                  </div>

                  {/* Deal Value */}
                  <div>
                    <label className="block text-[11px] font-semibold text-slate-500 uppercase mb-1">
                      {fieldLabels.deal_value || "Deal / Trade Value"} ({currency === "AED" ? "AED" : currency === "INR" ? "₹" : "$"})
                    </label>
                    <div className="relative">
                      <span className="absolute inset-y-0 left-0 pl-2.5 flex items-center pointer-events-none text-slate-400 text-xs font-bold">
                        {currency === "AED" ? "AED" : currency === "INR" ? "₹" : "$"}
                      </span>
                      <input
                        type="number"
                        min="0"
                        step={currency === "INR" ? "5000" : "500"}
                        value={dealValue}
                        onChange={(e) => setDealValue(Number(e.target.value))}
                        className={`w-full ${currency === "AED" ? "pl-11" : "pl-6"} pr-3 py-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg text-xs font-bold text-emerald-600 dark:text-emerald-400 focus:ring-2 focus:ring-primary/50`}
                      />
                    </div>
                  </div>

                  {/* Assigned Rep Dropdown with RBAC Matrix */}
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <label className="block text-[11px] font-semibold text-slate-500 uppercase">
                        Assigned Rep
                      </label>
                      {isAgent && (
                        <span className="text-[10px] text-amber-500 flex items-center gap-0.5" title="Sales Agents cannot reassign leads">
                          <Lock className="w-2.5 h-2.5" /> Read-only
                        </span>
                      )}
                    </div>
                    <select
                      value={assignedRep}
                      disabled={isAgent}
                      onChange={(e) => setAssignedRep(e.target.value)}
                      className="w-full px-3 py-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg text-xs font-semibold text-slate-800 dark:text-slate-100 focus:ring-2 focus:ring-primary/50 disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer"
                    >
                      <option value="">Unassigned</option>
                      {selectableMembers.map((m) => (
                        <option key={m.id} value={m.full_name}>
                          {m.full_name} ({formatRoleLabel(m.role)})
                        </option>
                      ))}
                      {assignedRep && !selectableMembers.some((m) => m.full_name.toLowerCase() === assignedRep.toLowerCase()) && (
                        <option value={assignedRep}>{assignedRep} (Assigned)</option>
                      )}
                    </select>
                  </div>
                </div>

                {/* Multi-Industry & Trading Specific Fields */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3.5 pt-2 border-t border-slate-200/60 dark:border-slate-800/60">
                  <div>
                    <label className="block text-[11px] font-semibold text-slate-500 uppercase mb-1">
                      {fieldLabels.service_type || "Product / Commodity"}
                    </label>
                    <input
                      type="text"
                      placeholder="e.g. Steel Coils, Basmati Rice, SaaS"
                      value={serviceType}
                      onChange={(e) => setServiceType(e.target.value)}
                      className="w-full px-3 py-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg text-xs font-medium text-slate-800 dark:text-slate-100 focus:ring-2 focus:ring-primary/50"
                    />
                  </div>

                  <div>
                    <label className="block text-[11px] font-semibold text-slate-500 uppercase mb-1">
                      {fieldLabels.quantity || "Quantity / Volume"}
                    </label>
                    <input
                      type="number"
                      placeholder="e.g. 500"
                      value={quantity}
                      onChange={(e) => setQuantity(e.target.value)}
                      className="w-full px-3 py-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg text-xs font-medium text-slate-800 dark:text-slate-100 focus:ring-2 focus:ring-primary/50"
                    />
                  </div>

                  <div>
                    <label className="block text-[11px] font-semibold text-slate-500 uppercase mb-1">
                      {fieldLabels.unit || "Unit of Measure"}
                    </label>
                    <input
                      type="text"
                      placeholder="e.g. MT, Bags, Lots, Barrels"
                      value={unit}
                      onChange={(e) => setUnit(e.target.value)}
                      className="w-full px-3 py-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg text-xs font-medium text-slate-800 dark:text-slate-100 focus:ring-2 focus:ring-primary/50"
                    />
                  </div>

                  <div>
                    <label className="block text-[11px] font-semibold text-slate-500 uppercase mb-1">
                      {fieldLabels.country_or_city || "Destination Port / City"}
                    </label>
                    <input
                      type="text"
                      placeholder="e.g. Nhava Sheva Port, Dubai"
                      value={countryOrCity}
                      onChange={(e) => setCountryOrCity(e.target.value)}
                      className="w-full px-3 py-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg text-xs font-medium text-slate-800 dark:text-slate-100 focus:ring-2 focus:ring-primary/50"
                    />
                  </div>
                </div>

                {/* Follow-up Date & Notes */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-2">
                  <div>
                    <label className="block text-[11px] font-semibold text-slate-500 uppercase mb-1">
                      Next Follow-up Date
                    </label>
                    <input
                      type="date"
                      value={followupDate}
                      onChange={(e) => setFollowupDate(e.target.value)}
                      className="w-full px-3 py-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg text-xs text-slate-800 dark:text-slate-100 focus:ring-2 focus:ring-primary/50"
                    />
                  </div>

                  <div className="sm:col-span-2">
                    <label className="block text-[11px] font-semibold text-slate-500 uppercase mb-1">
                      {fieldLabels.notes || "Commercial Notes & Incoterms"}
                    </label>
                    <textarea
                      rows={2}
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      placeholder="Add CIF/FOB terms, LC at sight notes, specifications..."
                      className="w-full px-3 py-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg text-xs text-slate-800 dark:text-slate-100 focus:ring-2 focus:ring-primary/50"
                    />
                  </div>
                </div>

                {/* Dynamic Custom Workspace Fields */}
                {(() => {
                  const schemaCustomFields = (workspaceConfig?.fields || []).filter(
                    (f) => f.key.startsWith("custom_") && f.visible !== false
                  );
                  const existingCustomKeys = Object.keys(customFieldsData);
                  const extraFields = existingCustomKeys
                    .filter((k) => !schemaCustomFields.some((f) => f.key === k))
                    .map((k) => ({
                      key: k,
                      label: k.replace(/^custom_/, "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()),
                      placeholder: `Enter ${k}`,
                      type: "text",
                      visible: true,
                    }));
                  const allCustom = [...schemaCustomFields, ...extraFields];

                  if (allCustom.length === 0) return null;

                  return (
                    <div className="pt-2 border-t border-slate-200/60 dark:border-slate-800/60">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-[11px] font-bold text-cyan-600 dark:text-cyan-400 uppercase tracking-wider flex items-center gap-1.5">
                          ✨ Custom Workspace Fields
                        </span>
                        <span className="text-[10px] text-cyan-600/80 dark:text-cyan-400/80 font-medium bg-cyan-500/10 border border-cyan-500/20 px-2 py-0.5 rounded-full">
                          Dynamic Schema
                        </span>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3.5">
                        {allCustom.map((field) => (
                          <div key={field.key}>
                            <label className="block text-[11px] font-semibold text-slate-500 uppercase mb-1">
                              {field.label}
                            </label>
                            <input
                              type="text"
                              value={customFieldsData[field.key] || ""}
                              onChange={(e) => setCustomFieldsData((prev) => ({ ...prev, [field.key]: e.target.value }))}
                              placeholder={field.placeholder || `Enter ${field.label}`}
                              className="w-full px-3 py-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg text-xs font-medium text-slate-800 dark:text-slate-100 focus:ring-2 focus:ring-cyan-500/50"
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })()}

                <div className="flex flex-wrap items-center justify-between gap-3 pt-2">
                  <div className="flex items-center gap-2">
                    {isPlatformAdmin && (
                      <button
                        type="button"
                        onClick={handleDispatchBalanceFollowUp}
                        disabled={balanceSending}
                        className="text-xs px-3.5 py-1.5 font-bold flex items-center gap-1.5 cursor-pointer rounded-lg bg-amber-500/10 text-amber-700 dark:text-amber-400 border border-amber-500/20 hover:bg-amber-500/20 transition-all shadow-sm"
                        title="Dispatch Milestone 2 Final Balance (50%) & 3-Month Retainer Invoice via WhatsApp"
                      >
                        {balanceSending ? (
                          <div className="w-3.5 h-3.5 border-2 border-amber-500/30 border-t-amber-500 rounded-full animate-spin" />
                        ) : (
                          <CreditCard className="w-3.5 h-3.5" />
                        )}
                        Follow-Up Milestone 2 &amp; 3-Mo Retainer
                      </button>
                    )}
                    {balanceSuccess && (
                      <span className="text-xs font-semibold text-emerald-600 dark:text-emerald-400 flex items-center gap-1">
                        <CheckCircle2 className="w-3.5 h-3.5" /> {balanceSuccess}
                      </span>
                    )}
                  </div>

                  <Button
                    onClick={handleSave}
                    disabled={saving}
                    size="sm"
                    className="text-xs px-4 py-1.5 font-bold flex items-center gap-1.5 cursor-pointer shadow"
                  >
                    {saving ? (
                      <div className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    ) : (
                      <Save className="w-3.5 h-3.5" />
                    )}
                    Save Pipeline Updates
                  </Button>
                </div>
              </div>

              {/* Scope & Requirement Summary */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Contact & Company Details */}
                <div className="p-5 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/40 space-y-3">
                  <h4 className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 flex items-center gap-1.5">
                    <UserCheck className="w-3.5 h-3.5 text-primary" /> Contact Details
                  </h4>
                  <table className="w-full text-xs">
                    <tbody>
                      <tr className="border-b border-slate-100 dark:border-slate-800/60">
                        <td className="py-2 text-slate-400 w-28">Email</td>
                        <td className="py-2 font-medium">{lead.email}</td>
                      </tr>
                      <tr className="border-b border-slate-100 dark:border-slate-800/60">
                        <td className="py-2 text-slate-400">Phone</td>
                        <td className="py-2 font-medium">{lead.phone || "Not provided"}</td>
                      </tr>
                      <tr className="border-b border-slate-100 dark:border-slate-800/60">
                        <td className="py-2 text-slate-400">Company</td>
                        <td className="py-2 font-medium">{lead.company_name || "-"}</td>
                      </tr>
                      <tr>
                        <td className="py-2 text-slate-400">Location</td>
                        <td className="py-2 font-medium">{lead.country_or_city || "Not provided"}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                {/* Project Parameters & Marketing Attribution */}
                <div className="p-5 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/40 space-y-3">
                  <h4 className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 flex items-center gap-1.5">
                    <Globe className="w-3.5 h-3.5 text-primary" /> Marketing & Attribution
                  </h4>
                  <table className="w-full text-xs">
                    <tbody>
                      <tr className="border-b border-slate-100 dark:border-slate-800/60">
                        <td className="py-2 text-slate-400 w-28">UTM Source</td>
                        <td className="py-2 font-mono font-medium text-primary">{lead.utm_source || "direct / organic"}</td>
                      </tr>
                      <tr className="border-b border-slate-100 dark:border-slate-800/60">
                        <td className="py-2 text-slate-400">UTM Campaign</td>
                        <td className="py-2 font-mono font-medium">{lead.utm_campaign || "-"}</td>
                      </tr>
                      <tr className="border-b border-slate-100 dark:border-slate-800/60">
                        <td className="py-2 text-slate-400">Budget Scope</td>
                        <td className="py-2 font-semibold text-emerald-600 dark:text-emerald-400">{lead.budget_range || "-"}</td>
                      </tr>
                      <tr>
                        <td className="py-2 text-slate-400">Timeline</td>
                        <td className="py-2 font-medium">{lead.timeline || "-"}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Project Scope / Message Payload */}
              <div className="p-5 rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-950/40 space-y-2">
                <h4 className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 flex items-center gap-1.5">
                  <FileText className="w-3.5 h-3.5 text-primary" /> Project Scope / Inquiry Content
                </h4>
                <div className="p-4 rounded-lg bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 text-xs font-mono whitespace-pre-wrap leading-relaxed max-h-56 overflow-y-auto">
                  {lead.project_details || "No custom message or calculator summary was provided."}
                </div>
              </div>

              {/* Chronological Audit Trail Timeline */}
              <div className="p-5 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/40 space-y-4">
                <h4 className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 flex items-center gap-1.5">
                  <History className="w-3.5 h-3.5 text-primary" /> Chronological Audit Trail ({auditLogs.length} Events)
                </h4>

                {auditLogs.length === 0 ? (
                  <p className="text-xs text-slate-400 italic">No audit records found.</p>
                ) : (
                  <div className="relative pl-6 space-y-4 before:absolute before:left-2 before:top-2 before:bottom-2 before:w-0.5 before:bg-slate-200 dark:before:bg-slate-800">
                    {auditLogs.map((log) => {
                      const isAutomation = log.action.includes("Stage Automation") || log.actor_name.includes("Workflow");
                      return (
                        <div key={log.id} className="relative group">
                          <span
                            className={`absolute -left-[23px] top-1.5 w-2.5 h-2.5 rounded-full ring-4 ring-white dark:ring-slate-900 ${
                              isAutomation ? "bg-amber-500 animate-pulse" : "bg-primary"
                            }`}
                          />
                          <div className="flex flex-col sm:flex-row sm:items-center justify-between text-xs gap-1">
                            <div className="flex items-center gap-1.5 flex-wrap font-semibold text-slate-900 dark:text-slate-200">
                              <span>{log.action}</span>
                              {isAutomation && (
                                <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-600 dark:text-amber-400 font-bold border border-amber-500/30">
                                  ⚡ Bot Automation
                                </span>
                              )}
                              <span className="font-normal text-slate-400 text-[11px]">
                                by <strong className="text-slate-600 dark:text-slate-300">{log.actor_name}</strong>
                              </span>
                            </div>
                            <span className="text-[11px] text-slate-400 font-mono">
                              {new Date(log.timestamp).toLocaleString()}
                            </span>
                          </div>
                          {(log.old_value || log.new_value) && (
                            <div
                              className={`mt-1.5 text-[11px] p-2.5 rounded-lg border ${
                                isAutomation
                                  ? "bg-amber-500/5 dark:bg-amber-500/10 border-amber-500/20 text-amber-900 dark:text-amber-200"
                                  : "bg-slate-50 dark:bg-slate-950 border-slate-100 dark:border-slate-800 text-slate-500 dark:text-slate-400"
                              }`}
                            >
                              {log.old_value && (
                                <div>
                                  <span className="opacity-60">Previous:</span> {log.old_value}
                                </div>
                              )}
                              {log.new_value && (
                                <div className="font-medium mt-0.5">
                                  <span className={isAutomation ? "text-amber-600 dark:text-amber-400 font-bold" : "text-primary"}>
                                    {isAutomation ? "⚡ Automated Action:" : "Updated:"}
                                  </span>{" "}
                                  {log.new_value}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </>
          ) : null}
        </div>

        {/* Modal Bottom Footer */}
        <div className="flex items-center justify-between px-6 py-3 border-t border-slate-200 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/50">
          <div className="flex items-center gap-3">
            {!isAgent && (
              <button
                type="button"
                onClick={handleDeleteLead}
                disabled={deleting}
                className="text-xs text-red-500 hover:text-red-700 dark:hover:text-red-400 flex items-center gap-1.5 font-semibold transition-colors cursor-pointer disabled:opacity-50"
              >
                <Trash2 className="w-3.5 h-3.5" />
                <span>{deleting ? "Deleting Lead..." : "Delete Lead"}</span>
              </button>
            )}
            <span className="text-xs text-slate-400 hidden sm:inline">
              • Last Updated: {lead?.updated_at ? new Date(lead.updated_at).toLocaleString() : "-"}
            </span>
          </div>
          <Button onClick={onClose} variant="outline" size="sm" className="text-xs cursor-pointer">
            Close
          </Button>
        </div>

      </div>
    </div>
  );
}
